import {
  BlockContentValue,
  Button,
  FormGenericErrorMessage,
  FormRow,
  ModalBody,
  ModalBodyTitle
} from "@silversea-ssc/ui-components-react";
import DisclaimerMessage from "components/DefaultSubscribeForm/DisclaimerMessage";
import {
  InputFormTitle,
  InputFormNameFirst,
  InputFormNameLast,
  InputFormEmail,
  InputFormWorkingWithAgent,
  HiddenTrackingFields
} from "components/DefaultSubscribeForm/fields";
import { useLeadForm } from "components/DefaultSubscribeForm/useLeadForm";
import AsyncForm from "components/Form/AsyncForm";
import React, { FC } from "react";
import { FormProvider } from "react-hook-form";
import * as styles from "./SubscribeModalForm.module.scss";
import RequiredFields from "components/DefaultSubscribeForm/RequiredFields";
import { BlockContent } from "components/BlockContent";
import { FormDisclaimerFragment } from "types/graphql-types";
import { Trans } from "@lingui/react/macro";
import { LeadFormData } from "components/DefaultSubscribeForm/types";
import { LeadAPIRequest } from "src/ssc-api/leadAPIClient";
import { LeadApiResponse } from "@silversea-ssc/dotcom-api-types/Lead";
import PhoneInputControl from "components/PhoneInputControl";
import { TokenValues } from "@silversea-ssc/dotcom-read-model-types/Tokens";

interface SubscribeModalFormProps {
  title: BlockContentValue | undefined;
  content: BlockContentValue | undefined;
  email?: string;
  onSubmitSuccess: (
    formData: LeadFormData,
    request: LeadAPIRequest,
    response: LeadApiResponse
  ) => void;
  formName: string;
  formDisclaimerContent: FormDisclaimerFragment | undefined;
  tokens?: TokenValues;
}

const SubscribeModalForm: FC<
  React.PropsWithChildren<SubscribeModalFormProps>
> = ({
  email,
  onSubmitSuccess,
  title,
  content,
  formName,
  formDisclaimerContent,
  tokens
}) => {
  return (
    <ModalBody className={styles.body} paddingBottom>
      <header className={styles.header}>
        <ModalBodyTitle>
          <BlockContent value={title} tokens={tokens} />
        </ModalBodyTitle>
        <div>
          <BlockContent value={content} tokens={tokens} />
        </div>
      </header>
      <ActualSubscribeModalForm
        onSubmitSuccess={onSubmitSuccess}
        email={email}
        formName={formName}
        dataContentName="sfo_form"
        formDisclaimerContent={formDisclaimerContent}
      />
    </ModalBody>
  );
};

export default SubscribeModalForm;

type Pros = {
  email: string | undefined;
  onSubmitSuccess: (
    formData: LeadFormData,
    request: LeadAPIRequest,
    response: LeadApiResponse
  ) => void;
  formName: string;
  dataContentName: string;
  formDisclaimerContent: FormDisclaimerFragment | undefined;
};
export const ActualSubscribeModalForm: FC<Pros> = ({
  email,
  onSubmitSuccess,
  formName,
  dataContentName,
  formDisclaimerContent
}) => {
  const { formMethods, onSubmit } = useLeadForm({
    askImNotTraveAgent: false,
    askWorkWithAgent: true,
    onSubmitSuccess,
    requestSource: "SFO",
    requestType: "SFO",
    email,
    enableSubscribeEmail: true,
    formName
  });

  const { formState, register } = formMethods;

  return (
    <FormProvider {...formMethods}>
      <AsyncForm
        name={formName}
        onSubmit={onSubmit}
        dataContentName={dataContentName}
      >
        <div>
          <HiddenTrackingFields
            register={register}
            brochureCode={false}
            enableSubscribeEmail={true}
            cruiseSuiteInfo={false}
          />

          <InputFormEmail register={register} />

          <FormRow columns="3">
            <InputFormTitle register={register} />
            <InputFormNameFirst register={register} />
            <InputFormNameLast register={register} />
          </FormRow>

          <PhoneInputControl
            name="Phone"
            id="subscribe-modal-phone"
            required={false}
          />

          <RequiredFields />

          <InputFormWorkingWithAgent register={register} />
        </div>
        <div className={styles.disclaimerText}>
          <DisclaimerMessage content={formDisclaimerContent} />
        </div>

        <div className={styles.footer}>
          {!formState.isValid && formState.isSubmitted && (
            <FormGenericErrorMessage message="One or more fields have an error" />
          )}
          <Button
            className={styles.bodyFooterButton}
            renderAs="button"
            type="submit"
            full
            loading={formState.isSubmitting}
            data-testid="submit"
            dataContentPiece="submit_button"
          >
            <Trans>SUBSCRIBE</Trans>
          </Button>
        </div>
      </AsyncForm>
    </FormProvider>
  );
};
